import { useState, useEffect, useCallback } from 'react'
import { trainingAPI, loraFolderAPI, type UserLora, type TrainingJob, type LoraFolder } from '@/lib/api/training'

export interface LoraListItem {
  id: string
  name: string
  triggerWord: string | null
  baseModel: string
  modelType: string
  thumbnailUrl: string | null
  folderId: string | null
  status: 'ready' | 'training' | 'failed'
  createdAt: string
  source: 'trained' | 'imported'
  sourceType: 'user_lora' | 'training_job'
}

export function useUserLoras() {
  const [models, setModels] = useState<LoraListItem[]>([])
  const [folders, setFolders] = useState<LoraFolder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchModels = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [lorasResult, jobsResult, foldersResult] = await Promise.all([
        trainingAPI.listLoras('all'),
        trainingAPI.listJobs(),
        loraFolderAPI.list(),
      ])

      // Completed LoRAs
      const readyModels: LoraListItem[] = lorasResult.loras.map((lora: UserLora) => ({
        id: lora.id,
        name: lora.name,
        triggerWord: lora.triggerWord,
        baseModel: lora.baseModel,
        modelType: lora.modelType,
        thumbnailUrl: lora.thumbnailUrl,
        folderId: lora.folderId ?? null,
        status: 'ready' as const,
        createdAt: lora.createdAt,
        source: 'trained' as const,
        sourceType: 'user_lora' as const,
      }))

      // In-progress/failed training jobs (not yet converted to LoRAs)
      const loraJobIds = new Set(lorasResult.loras.map((l: UserLora) => l.trainingJob?.id).filter(Boolean))
      const inProgressModels: LoraListItem[] = (jobsResult.jobs as TrainingJob[])
        .filter((job: TrainingJob) => !loraJobIds.has(job.id) && ['pending', 'uploading', 'queued', 'training', 'failed'].includes(job.status))
        .map((job: TrainingJob) => {
          const endpointParts = job.endpointId.split('/')
          const baseModel = endpointParts[1]?.replace(/-lora.*|-training.*/g, '') ?? 'unknown'
          return {
            id: job.id,
            name: job.name,
            triggerWord: null,
            baseModel,
            modelType: 'image',
            thumbnailUrl: null,
            folderId: null,
            status: job.status === 'failed' ? 'failed' as const : 'training' as const,
            createdAt: job.createdAt,
            source: 'trained' as const,
            sourceType: 'training_job' as const,
          }
        })

      setModels([...inProgressModels, ...readyModels])
      setFolders(foldersResult)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load models'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchModels()
  }, [fetchModels])

  // Refetch when a new training job is created
  useEffect(() => {
    const handler = () => void fetchModels()
    window.addEventListener('training-job-created', handler)
    return () => window.removeEventListener('training-job-created', handler)
  }, [fetchModels])

  const renameLora = useCallback(async (id: string, name: string) => {
    try {
      await trainingAPI.renameLora(id, name)
      setModels((prev) => prev.map((m) => m.id === id ? { ...m, name } : m))
      return true
    } catch {
      return false
    }
  }, [])

  const deleteLora = useCallback(async (id: string) => {
    const model = models.find((m) => m.id === id)
    if (!model) return false
    try {
      if (model.sourceType === 'training_job') {
        await trainingAPI.deleteTrainingJob(id)
      } else {
        await trainingAPI.deleteLora(id)
      }
      setModels((prev) => prev.filter((m) => m.id !== id))
      return true
    } catch {
      return false
    }
  }, [models])

  const moveLoraToFolder = useCallback(async (loraId: string, folderId: string | null) => {
    try {
      await trainingAPI.moveLoraToFolder(loraId, folderId)
      setModels((prev) => prev.map((m) => m.id === loraId ? { ...m, folderId } : m))
      // Update folder counts
      setFolders((prev) => prev.map((f) => {
        const count = folderId === f.id
          ? f.loraCount + 1
          : f.loraCount - (models.find((m) => m.id === loraId)?.folderId === f.id ? 1 : 0)
        return { ...f, loraCount: Math.max(0, count) }
      }))
      return true
    } catch {
      return false
    }
  }, [models])

  const createFolder = useCallback(async (name: string) => {
    try {
      const folder = await loraFolderAPI.create(name)
      setFolders((prev) => [folder, ...prev])
      return folder
    } catch {
      return null
    }
  }, [])

  const renameFolder = useCallback(async (id: string, name: string) => {
    try {
      await loraFolderAPI.rename(id, name)
      setFolders((prev) => prev.map((f) => f.id === id ? { ...f, name } : f))
      return true
    } catch {
      return false
    }
  }, [])

  const deleteFolder = useCallback(async (id: string) => {
    try {
      await loraFolderAPI.remove(id)
      setFolders((prev) => prev.filter((f) => f.id !== id))
      // Loras in this folder move to root
      setModels((prev) => prev.map((m) => m.folderId === id ? { ...m, folderId: null } : m))
      return true
    } catch {
      return false
    }
  }, [])

  return {
    models, folders, loading, error,
    refetch: fetchModels,
    renameLora, deleteLora, moveLoraToFolder,
    createFolder, renameFolder, deleteFolder,
  }
}
